// Import Helpers
import { generateCleanUrlParams } from 'services/Helpers';
// Import Configs
import {
    CRMRequestService,
    CRMRequestDownloadLinkService,
    postCancelableRequest,
    deleteCancelableRequest,
    getCancelableRequest,
} from './http.config';
// Import Types
import type { Campaign, CampaignExecuteResult, CampaignListItem, CampaignPreview, DataWithPaging } from 'types';

const CampaignHttpService = {
    getCampaignList: () => {
        return postCancelableRequest<DataWithPaging<CampaignListItem>>({
            requestService: CRMRequestService,
            endpointPath: () => '/Campaign/List',
        });
    },
    getCampaignById: () => {
        return getCancelableRequest<Campaign>({
            requestService: CRMRequestService,
            defaultParams: { isClone: false },
            endpointPath: ({ campaignId, approvalRequestId, pointInTime, isClone }) =>
                `/Campaign/Get${generateCleanUrlParams({ id: campaignId, approvalRequestId, pointInTime, isClone })}`,
        });
    },
    getCampaignPreviewById: () => {
        return getCancelableRequest<CampaignPreview>({
            requestService: CRMRequestService,
            endpointPath: ({ campaignId }) => `Campaign/Preview?id=${campaignId}`,
        });
    },
    getCampaignBonusesReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Campaign/GetCampaignBonusesReport',
        });
    },
    getCampaignAwardDetails: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Campaign/GetCampaignAwardDetails',
        });
    },
    getCampaignEmailActivity: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Campaign/GetTrackActionData',
        });
    },
    getCampaignEmailsReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Campaign/GetCampaignEmailsReport',
        });
    },
    getCampaignEmailTrackInfoOpen: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Campaign/GetDataEmailTrackInfoOpen',
        });
    },
    getCampaignABTestResult: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ CampaignId }) => `/Campaign/GetABTestResult?campaignId=${CampaignId}`,
        });
    },
    getCampaignBonusChartsData: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Campaign/GetBonusChartsData',
        });
    },
    getCampaignEmailChartsData: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/Campaign/GetEmailChartsData',
        });
    },
    deleteCampaign: () => {
        return deleteCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ campaignId }) => `/Campaign/Delete?id=${campaignId}`,
        });
    },
    deleteCampaigns: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Campaign/Delete/`,
        });
    },
    archiveCampaign: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ campaignId }) => `Campaign/Archive?id=${campaignId}`,
        });
    },
    unarchiveCampaign: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ campaignId }) => `/Campaign/unArchive?id=${campaignId}`,
        });
    },
    archiveCampaigns: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `Campaign/Archive`,
        });
    },
    unarchiveCampaigns: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `/Campaign/unArchive`,
        });
    },
    stopSendCampaigns: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ campaignId }) => `Campaign/Stop?id=${campaignId}`,
        });
    },
    getCampaignIdGoalState: () => {
        return getCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ campaignId }) => `/Campaign/GetCampaignIdGoalState?campaignId=${campaignId}`,
        });
    },
    rerunCampaignExecuteResult: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ campaignExecuteResultId }) =>
                `/Campaign/RerunCampaignExecuteResult?campaignExecuteResultId=${campaignExecuteResultId}`,
        });
    },
    createCampaign: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => `Campaign/Create`,
        });
    },
    editCampaign: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ campaignId }) => `Campaign/Update${generateCleanUrlParams({ id: campaignId })}`,
        });
    },
    getCampaignExecuteResults: () => {
        return postCancelableRequest<DataWithPaging<CampaignExecuteResult>>({
            requestService: CRMRequestService,
            endpointPath: ({ campaignId, pointInTime }) =>
                `/Campaign/GetCampaignExecuteResults${generateCleanUrlParams({ campaignId, pointInTime })}`,
        });
    },
    setCampaignGoal: () => {
        return postCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: () => '/CampaignGoal/Create',
        });
    },
    deleteCampaignGoal: () => {
        return deleteCancelableRequest({
            requestService: CRMRequestService,
            endpointPath: ({ id }) => `/CampaignGoal/Delete?id=${id}`,
        });
    },
    downloadEmailReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestDownloadLinkService,
            endpointPath: ({ isDownload }) => `Campaign/DownloadEmailReport?isDownload=${isDownload}`,
        });
    },
    downloadEmailClickReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestDownloadLinkService,
            endpointPath: ({ isDownload }) => `Campaign/DownloadEmailClickReport?isDownload=${isDownload}`,
        });
    },
    downloadEmailOpenReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestDownloadLinkService,
            endpointPath: ({ isDownload }) => `Campaign/DownloadEmailOpenReport?isDownload=${isDownload}`,
        });
    },
    downloadBonusReport: () => {
        return postCancelableRequest({
            requestService: CRMRequestDownloadLinkService,
            endpointPath: ({ isDownload }) => `Campaign/DownloadBonusReport?isDownload=${isDownload}`,
        });
    },
};

export default CampaignHttpService;
